import React, { useState, useEffect } from "react";
import {
  ShieldAlert,
  Award,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  RefreshCw,
  Sparkles,
  FileText,
  ArrowRight,
} from "lucide-react";
import {
  Opportunity,
  Requirement,
  DraftSection,
  RedTeamReview,
  Answer,
  UserProfile,
  UnsupportedClaim,
} from "../../types";
import { subscribeToLatestRedTeamReview, runRedTeamReview } from "../../services/redTeamService";
import { subscribeToRequirements } from "../../services/requirementsService";
import { subscribeToDraftSections } from "../../services/draftsService";
import { subscribeToAnswers } from "../../services/answersService";
import { scanDraftSectionsForStaleAnswers } from "../../utils/freshnessScanner";
import { ExpandableCard } from "../common/ExpandableCard";
import { AskSmeModal } from "./AskSmeModal";

interface RedTeamTabProps {
  opportunity: Opportunity;
  user?: UserProfile | null;
  onSwitchTab?: (tab: string) => void;
}

export const RedTeamTab: React.FC<RedTeamTabProps> = ({ opportunity, onSwitchTab }) => {
  const [review, setReview] = useState<RedTeamReview | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [draftSections, setDraftSections] = useState<DraftSection[]>([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRunningReview, setIsRunningReview] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // SME Modal routing
  const [smeClaim, setSmeClaim] = useState<UnsupportedClaim | null>(null);
  const [isSmeModalOpen, setIsSmeModalOpen] = useState(false);

  // Subscribe to latest review
  useEffect(() => {
    const unsubscribe = subscribeToLatestRedTeamReview(
      opportunity.id,
      (latest) => {
        setReview(latest);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to subscribe to red team review:", err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [opportunity.id]);

  // Subscribe to requirements
  useEffect(() => {
    const unsubscribe = subscribeToRequirements(
      opportunity.id,
      (reqs) => setRequirements(reqs),
      (err) => console.warn("Failed to subscribe to requirements:", err),
    );
    return () => unsubscribe();
  }, [opportunity.id]);

  // Subscribe to draft sections
  useEffect(() => {
    const unsubscribe = subscribeToDraftSections(
      opportunity.id,
      (sections) => setDraftSections(sections),
      (err) => console.warn("Failed to subscribe to draft sections:", err),
    );
    return () => unsubscribe();
  }, [opportunity.id]);

  // Subscribe to library answers for draft freshness check
  useEffect(() => {
    const unsubscribe = subscribeToAnswers(
      (items) => setAnswers(items),
      (err) => console.warn("Failed to subscribe to answers in RedTeamTab:", err),
    );
    return () => unsubscribe();
  }, []);

  const handleRunReview = async () => {
    setErrorMessage(null);
    setIsRunningReview(true);
    try {
      const res = await runRedTeamReview({
        opportunityId: opportunity.id,
        opportunityTitle: opportunity.title,
        client: opportunity.client,
        requirements,
        draftSections,
      });
      setReview(res);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to run Evaluator Red Team review";
      setErrorMessage(msg);
    } finally {
      setIsRunningReview(false);
    }
  };

  const handleRouteClaimToSme = (claim: UnsupportedClaim) => {
    setSmeClaim(claim);
    setIsSmeModalOpen(true);
  };

  // Draft freshness findings
  const draftFreshnessFindings = scanDraftSectionsForStaleAnswers(draftSections, answers, [
    { id: opportunity.id, title: opportunity.title },
  ]);

  const getGradeBadge = (grade: "A" | "B" | "C" | "D" | "F") => {
    switch (grade) {
      case "A":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300";
      case "B":
        return "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border-teal-300";
      case "C":
        return "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300";
      case "D":
        return "bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-300 border-orange-300";
      case "F":
        return "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300";
    }
  };

  if (loading) {
    return (
      <div className="glass flex items-center justify-center rounded-2xl p-16 text-slate-400">
        <RefreshCw className="h-6 w-6 animate-spin text-[#2ba5b5]" />
        <span className="ml-3 text-sm">Loading Evaluator Red Team audit...</span>
      </div>
    );
  }

  // Pre-fill SME modal requirement
  const selectedReq = smeClaim?.sectionId
    ? requirements.find((r) => r.id === smeClaim.sectionId) || requirements[0]
    : requirements[0];

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {errorMessage && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
            <span>{errorMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="text-rose-600 hover:underline font-semibold"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header and Controls */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-[#2ba5b5]" />
            <span>Evaluator Red Team Audit</span>
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-2xl">
            Simulates a skeptical public-sector evaluation committee scoring your proposal draft
            against explicit RFP criteria and rigorously detecting unverified or unsupported claims.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRunReview}
            disabled={isRunningReview || draftSections.length === 0}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
          >
            {isRunningReview ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                <span>Simulating Evaluator Panel...</span>
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                <span>{review ? "Re-run Red Team Audit" : "Run Evaluator Review"}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {draftSections.length === 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            <span>
              No proposal sections have been drafted yet. Draft sections on the Drafting Board to
              enable the Evaluator Red Team panel audit.
            </span>
          </div>
          {onSwitchTab && (
            <button
              type="button"
              onClick={() => onSwitchTab("drafting")}
              className="btn-animate inline-flex items-center gap-1 rounded bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700"
            >
              <span>Go to Drafting Board</span>
              <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Empty State when no review exists */}
      {!review ? (
        <div className="glass flex flex-col items-center justify-center rounded-2xl border border-slate-200/80 p-12 text-center dark:border-slate-800/80">
          <div className="rounded-full bg-teal-50 p-3 text-[#2ba5b5] dark:bg-teal-950/60 dark:text-teal-400">
            <Award className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
            Evaluator Red Team Audit Has Not Been Run Yet
          </h3>
          <p className="mt-1 max-w-md text-xs text-slate-500 dark:text-slate-400">
            Before submitting your proposal, run the Red Team simulation. It grades your draft
            sections against RFP scoring rubrics, identifies ungrounded assertions, and prepares
            your team for evaluator clarification questions.
          </p>
          <button
            type="button"
            onClick={handleRunReview}
            disabled={isRunningReview || draftSections.length === 0}
            className="btn-animate mt-6 inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Launch Initial Red Team Simulation</span>
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Executive Score Banner */}
          <div className="glass rounded-xl border border-slate-200/80 p-6 dark:border-slate-800/80">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div
                  className={`flex h-20 w-20 flex-col items-center justify-center rounded-2xl border-2 font-bold shadow-xs ${getGradeBadge(
                    review.overallGrade,
                  )}`}
                >
                  <span className="text-3xl leading-none">{review.overallGrade}</span>
                  <span className="text-[11px] font-medium tracking-wider mt-0.5">GRADE</span>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-extrabold text-slate-900 dark:text-white">
                      {review.overallWeightedScore}%
                    </span>
                    <span className="text-xs uppercase font-semibold text-slate-400">
                      Evaluator Weighted Score
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Calculated from RFP evaluation criteria and public-sector scoring rubrics.
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                    <span>
                      Model: <strong>{review.modelUsed || "gemini-3.8-flash"}</strong>
                    </span>
                    <span>•</span>
                    <span>
                      Audited: {new Date(review.generatedAt).toLocaleDateString()} at{" "}
                      {new Date(review.generatedAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Metrics */}
              <div className="flex items-center gap-3">
                <div className="rounded-xl border border-slate-200/70 bg-white/70 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60 min-w-24">
                  <div className="text-lg font-bold text-slate-900 dark:text-white">
                    {review.criteriaScores.length}
                  </div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500">
                    Criteria Evaluated
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200/70 bg-white/70 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60 min-w-24">
                  <div
                    className={`text-lg font-bold ${
                      review.unsupportedClaims.length > 0 ? "text-rose-600" : "text-emerald-600"
                    }`}
                  >
                    {review.unsupportedClaims.length}
                  </div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500">
                    Claims Flagged
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200/70 bg-white/70 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60 min-w-24">
                  <div
                    className={`text-lg font-bold ${
                      review.keyRisks.length > 0 ? "text-amber-600" : "text-slate-600"
                    }`}
                  >
                    {review.keyRisks.length}
                  </div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500">
                    Key Risks
                  </div>
                </div>
              </div>
            </div>

            {/* Evaluator Summary Quote */}
            <div className="mt-5 rounded-lg border-l-4 border-l-[#2ba5b5] bg-teal-50/40 p-4 text-xs leading-relaxed text-slate-800 dark:bg-teal-950/20 dark:text-slate-200">
              <span className="font-bold text-[#0d7482] dark:text-teal-400 uppercase tracking-wider block mb-1">
                Evaluation Committee Executive Perspective:
              </span>
              {review.executiveSummary}
            </div>
          </div>

          {/* Section 1: Unsupported Claims Card */}
          <ExpandableCard
            title="Unsupported & Ungrounded Claims"
            badge={`${review.unsupportedClaims.length} Claims Flagged`}
            badgeColor={
              review.unsupportedClaims.length > 0
                ? "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
            }
            defaultExpanded={false}
            summary={
              <p className="pt-1 text-xs text-slate-600 dark:text-slate-300">
                {review.unsupportedClaims.length > 0
                  ? "Verbatim statements in your draft that lack verifiable metrics or supporting proof. Route to an SME for evidence."
                  : "All claims and technical commitments in your draft are grounded in verified library answers or RFP evidence."}
              </p>
            }
          >
            {review.unsupportedClaims.length === 0 ? (
              <div className="py-4 text-center text-xs text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span>Zero unsupported claims detected across all proposal sections.</span>
              </div>
            ) : (
              <div className="space-y-4 pt-2">
                {review.unsupportedClaims.map((claim, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-rose-200/80 bg-rose-50/30 p-4 text-xs dark:border-rose-900/60 dark:bg-rose-950/20"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="space-y-1.5 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800 dark:bg-rose-900/80 dark:text-rose-200">
                            Claim #{idx + 1}
                          </span>
                          {claim.sectionTitle && (
                            <span className="text-[11px] font-semibold text-slate-500">
                              in {claim.sectionTitle}
                            </span>
                          )}
                        </div>
                        <blockquote className="rounded-md border-l-2 border-rose-500 bg-white p-2.5 font-mono text-[11px] text-slate-800 italic dark:bg-slate-900 dark:text-slate-200">
                          &ldquo;{claim.claimText}&rdquo;
                        </blockquote>
                        <p className="text-slate-700 dark:text-slate-300">
                          <strong>Evaluator Concern:</strong> {claim.issue}
                        </p>
                        <p className="text-slate-600 dark:text-slate-400">
                          <strong>Evidence Needed:</strong> {claim.suggestedRemedy}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRouteClaimToSme(claim)}
                        className="btn-animate shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#2ba5b5] bg-white px-3 py-1.5 text-xs font-semibold text-[#0d7482] shadow-2xs hover:bg-teal-50 dark:border-teal-800 dark:bg-slate-900 dark:text-teal-300"
                      >
                        <HelpCircle className="h-3.5 w-3.5 text-[#2ba5b5]" />
                        <span>Ask SME for Evidence</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ExpandableCard>

          {/* Section 2: Criteria Breakdown */}
          <ExpandableCard
            title="Evaluation Criteria Breakdown"
            badge={`${review.criteriaScores.length} Criteria`}
            badgeColor="bg-[#2ba5b5]/10 text-[#0d7482] dark:bg-teal-950/60 dark:text-teal-300"
            defaultExpanded={false}
            summary={
              <p className="pt-1 text-xs text-slate-600 dark:text-slate-300">
                Detailed scores, strengths, weaknesses, and committee comments by RFP scoring
                criterion.
              </p>
            }
          >
            <div className="space-y-4 pt-2">
              {review.criteriaScores.map((crit) => (
                <div
                  key={crit.criterionId}
                  className="rounded-lg border border-slate-200 bg-white p-4 shadow-2xs dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-slate-100 pb-2.5 dark:border-slate-800">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                        {crit.criterionTitle}
                      </h4>
                      <span className="text-[11px] text-slate-400">
                        Weight: {crit.weight}% of proposal total
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-extrabold text-[#2ba5b5]">
                        {crit.rawScore} / 100
                      </span>
                      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        Weighted: {crit.weightedScore.toFixed(1)} pts
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 space-y-2 text-xs">
                    <p className="text-slate-700 dark:text-slate-300">
                      <strong>Evaluator Rationale:</strong> {crit.rationale}
                    </p>

                    {crit.evaluatorPerspective && (
                      <p className="italic text-slate-500 dark:text-slate-400">
                        &ldquo;{crit.evaluatorPerspective}&rdquo;
                      </p>
                    )}

                    <div className="grid gap-2 sm:grid-cols-2 pt-2">
                      {crit.strengths && crit.strengths.length > 0 && (
                        <div className="rounded-md bg-emerald-50/60 p-2.5 dark:bg-emerald-950/20">
                          <span className="font-semibold text-emerald-800 dark:text-emerald-300 block mb-1">
                            Strengths Noted:
                          </span>
                          <ul className="list-disc pl-4 space-y-0.5 text-emerald-900 dark:text-emerald-200">
                            {crit.strengths.map((s, idx) => (
                              <li key={idx}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {crit.weaknesses && crit.weaknesses.length > 0 && (
                        <div className="rounded-md bg-rose-50/60 p-2.5 dark:bg-rose-950/20">
                          <span className="font-semibold text-rose-800 dark:text-rose-300 block mb-1">
                            Areas Discounted / Weaknesses:
                          </span>
                          <ul className="list-disc pl-4 space-y-0.5 text-rose-900 dark:text-rose-200">
                            {crit.weaknesses.map((w, idx) => (
                              <li key={idx}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ExpandableCard>

          {/* Section 3: Key Risks & Evaluator Questions */}
          <ExpandableCard
            title="Committee Deliberation & Key Risks"
            badge={`${review.keyRisks.length} Risks Flagged`}
            badgeColor={
              review.keyRisks.length > 0
                ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300"
            }
            defaultExpanded={false}
            summary={
              <p className="pt-1 text-xs text-slate-600 dark:text-slate-300">
                Key areas where the evaluator panel feels our proposal might be vulnerable against
                competitor submissions.
              </p>
            }
          >
            {review.keyRisks.length === 0 ? (
              <p className="py-2 text-xs text-slate-500">No major disqualifying risks noted.</p>
            ) : (
              <ul className="space-y-2 pt-2 text-xs">
                {review.keyRisks.map((risk, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 rounded-lg border border-amber-200/60 bg-amber-50/30 p-2.5 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200"
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            )}
          </ExpandableCard>

          {/* Section 4: In-Draft Freshness Scanner Findings */}
          <ExpandableCard
            title="Draft Sections Freshness Check"
            badge={`${draftFreshnessFindings.length} Citation Findings`}
            badgeColor={
              draftFreshnessFindings.length > 0
                ? "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
            }
            defaultExpanded={false}
            summary={
              <p className="pt-1 text-xs text-slate-600 dark:text-slate-300">
                Deterministic code verification of library answers incorporated into this
                opportunity&apos;s draft sections.
              </p>
            }
          >
            {draftFreshnessFindings.length === 0 ? (
              <div className="py-4 text-center text-xs text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span>
                  All cited library answers in current draft sections are active and current.
                </span>
              </div>
            ) : (
              <div className="space-y-3 pt-2 text-xs">
                {draftFreshnessFindings.map((finding) => (
                  <div
                    key={finding.id}
                    className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200 flex items-start justify-between gap-3"
                  >
                    <div>
                      <span className="font-semibold block">{finding.targetTitle}</span>
                      <p className="mt-0.5 text-[11px] text-slate-700 dark:text-slate-300">
                        {finding.details}
                      </p>
                      <p className="mt-1 text-[11px] font-medium text-[#0d7482] dark:text-teal-300">
                        Suggested Action: {finding.suggestedAction}
                      </p>
                    </div>

                    {onSwitchTab && (
                      <button
                        type="button"
                        onClick={() => onSwitchTab("drafting")}
                        className="btn-animate shrink-0 inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200"
                      >
                        <FileText className="h-3 w-3" />
                        <span>Edit Section</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ExpandableCard>
        </div>
      )}

      {/* SME Evidence Routing Modal */}
      {selectedReq && (
        <AskSmeModal
          isOpen={isSmeModalOpen}
          onClose={() => {
            setIsSmeModalOpen(false);
            setSmeClaim(null);
          }}
          opportunity={opportunity}
          requirement={selectedReq}
          initialQuestion={
            smeClaim
              ? `Can you provide concrete evidence, metrics, or proof for our proposal assertion: "${smeClaim.claimText}"? Needed: ${smeClaim.suggestedRemedy || smeClaim.issue}`
              : undefined
          }
        />
      )}
    </div>
  );
};
